<script setup lang="ts">

const LARGEST_VIEWPORT = 1920; // Retina sizes will take care of 4k (2560px) and other huge screens

/**
 * @todo Merge this with the BaseImage component, so that we automatically use Sanity provider if it's a Sanity
 *       image object, or the Local (ipx) provider if it's a local image on the filesystem.
 *       See: https://image.nuxt.com/providers/sanity
 *
 * When using Local Images, place the largest size image possible (without upscaling it) into the /public/images directory.
 * This `LocalImage` component will then optimise it automatically.
 *
 * [1] Must be a string to an image within the `public` directory, starting with a /.
 *     e.g. `/images/test.png` will include an image from `/public/images/test.png`.
 *     Do not include "`/public`" in the src.
 * [2] For full-width images, use the actual height/width of the image file.
 *     For images being set to a specific size, use the intrinsic size (the size it will appear on screen).
 * [3] Above the fold images should be 'eager'. All else should be 'lazy'. Defaults to 'lazy'.
 * [4] Set maxWidth to the largest width the image can assume in the design, or undefined to occupy the whole width
 * [5] Pass in custom <img> element `sizes` attribute, or leave undefined to generate sensible sizes
 * [6] Image format. Recommend 'webp'. Leave undefined for auto resolution
 * [7] The fit property specifies the size of the images. There are five standard values you can use with this property.
 */
const props = withDefaults(defineProps<{
  src: string; /* [1] */
  height: number; /* [2] */
  width: number; /* [2] */
  alt?: string;
  loading?: 'lazy' | 'eager'; /* [3] */
  maxWidth?: number, /* [4] */
  sizes?: string; /* [5] */
  quality?: number; /* [5] */
  format?: 'webp' | 'avif' | 'jpeg' | 'jpg' | 'png' | 'gif'; /* [6] */
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside'; /* [7] */
}>(), {
  alt: '',
  loading: 'lazy',
  maxWidth: LARGEST_VIEWPORT,
  sizes: undefined,
  quality: 100,
  format: 'webp',
  fit: 'cover',
});

const imageDimensions = reactive({
  width: props.width,
  height: props.height,
  aspectRatio: props.width / props.height,
});

const imgSizes = props.maxWidth === LARGEST_VIEWPORT
  ? 'sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw'
  : props.sizes || `(max-width: ${props.maxWidth}px) 100vw, ${props.maxWidth}px`;
</script>

<template>
  <NuxtImg
    v-if="src"
    :src="src"
    :sizes="imgSizes"
    :alt="props.alt || ''"
    :width="imageDimensions.width"
    :height="imageDimensions.height"
    :quality="props.quality"
    :format="props.format"
    :fit="fit"
    v-bind="$attrs"
    :loading="props.loading || 'lazy'"
    :fetchpriority="loading === 'eager' ? 'high' : undefined"
  />
</template>

<style scoped>
img {
  height: auto;
}
</style>
